import React, { Component } from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import InfoIcon from "@material-ui/icons/Info";
import FileCopyIcon from "@material-ui/icons/FileCopy";

const styles = (theme) => ({
  root: {
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontSize: "1rem",
    fontWeight: "bold",
    color: "#0d47a1",
    marginBottom: theme.spacing(1),
  },
  dayRow: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 0),
    borderBottom: "1px solid #e0e0e0",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  dayLabel: {
    minWidth: 100,
    fontWeight: 500,
  },
  timeField: {
    width: 110,
    marginRight: theme.spacing(1),
  },
  timezoneSelect: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  closedDay: {
    opacity: 0.5,
  },
  copyButton: {
    marginLeft: theme.spacing(1),
  },
  infoText: {
    fontSize: "0.85rem",
    color: "#666",
    marginTop: theme.spacing(1),
  },
});

// Dias da semana em português
const DAYS_OF_WEEK = [
  { key: "sunday", label: "Domingo", short: "Dom" },
  { key: "monday", label: "Segunda-feira", short: "Seg" },
  { key: "tuesday", label: "Terça-feira", short: "Ter" },
  { key: "wednesday", label: "Quarta-feira", short: "Qua" },
  { key: "thursday", label: "Quinta-feira", short: "Qui" },
  { key: "friday", label: "Sexta-feira", short: "Sex" },
  { key: "saturday", label: "Sábado", short: "Sáb" },
];

// Timezones mais comuns do Brasil
const BRAZIL_TIMEZONES = [
  { value: "America/Sao_Paulo", label: "Brasília (GMT-3)" },
  { value: "America/Fortaleza", label: "Fortaleza (GMT-3)" },
  { value: "America/Recife", label: "Recife (GMT-3)" },
  { value: "America/Bahia", label: "Salvador (GMT-3)" },
  { value: "America/Manaus", label: "Manaus (GMT-4)" },
  { value: "America/Cuiaba", label: "Cuiabá (GMT-4)" },
  { value: "America/Porto_Velho", label: "Porto Velho (GMT-4)" },
  { value: "America/Rio_Branco", label: "Rio Branco (GMT-5)" },
  { value: "America/Noronha", label: "Fernando de Noronha (GMT-2)" },
];

// Horários padrão para nova área
const DEFAULT_BUSINESS_HOURS = {
  timezone: "America/Sao_Paulo",
  schedule: {
    sunday: { enabled: false, open: "08:00", close: "18:00" },
    monday: { enabled: true, open: "08:00", close: "18:00" },
    tuesday: { enabled: true, open: "08:00", close: "18:00" },
    wednesday: { enabled: true, open: "08:00", close: "18:00" },
    thursday: { enabled: true, open: "08:00", close: "18:00" },
    friday: { enabled: true, open: "08:00", close: "18:00" },
    saturday: { enabled: true, open: "08:00", close: "12:00" },
  },
};

class BusinessHoursEditor extends Component {
  constructor(props) {
    super(props);

    // Inicializa com valores padrão ou valores existentes
    const initialHours = props.value || DEFAULT_BUSINESS_HOURS;

    this.state = {
      timezone: initialHours.timezone || DEFAULT_BUSINESS_HOURS.timezone,
      schedule: initialHours.schedule || DEFAULT_BUSINESS_HOURS.schedule,
    };
  }

  componentDidUpdate(prevProps) {
    // Atualiza state quando props mudam (para modo de edição)
    if (prevProps.value !== this.props.value && this.props.value) {
      this.setState({
        timezone: this.props.value.timezone || DEFAULT_BUSINESS_HOURS.timezone,
        schedule: this.props.value.schedule || DEFAULT_BUSINESS_HOURS.schedule,
      });
    }
  }

  handleTimezoneChange = (event) => {
    const timezone = event.target.value;
    this.setState({ timezone }, this.notifyChange);
  };

  handleDayToggle = (dayKey) => {
    this.setState(
      (prevState) => ({
        schedule: {
          ...prevState.schedule,
          [dayKey]: {
            ...prevState.schedule[dayKey],
            enabled: !prevState.schedule[dayKey].enabled,
          },
        },
      }),
      this.notifyChange
    );
  };

  handleTimeChange = (dayKey, field, value) => {
    this.setState(
      (prevState) => ({
        schedule: {
          ...prevState.schedule,
          [dayKey]: {
            ...prevState.schedule[dayKey],
            [field]: value,
          },
        },
      }),
      this.notifyChange
    );
  };

  copyToAllDays = (sourceDayKey) => {
    const sourceDay = this.state.schedule[sourceDayKey];

    this.setState(
      (prevState) => {
        const newSchedule = { ...prevState.schedule };
        DAYS_OF_WEEK.forEach((day) => {
          if (day.key !== sourceDayKey) {
            newSchedule[day.key] = {
              ...newSchedule[day.key],
              open: sourceDay.open,
              close: sourceDay.close,
            };
          }
        });
        return { schedule: newSchedule };
      },
      this.notifyChange
    );
  };

  notifyChange = () => {
    const { onChange } = this.props;
    const { timezone, schedule } = this.state;

    if (onChange) {
      onChange({ timezone, schedule });
    }
  };

  render() {
    const { classes, compact } = this.props;
    const { timezone, schedule } = this.state;

    return (
      <Box className={classes.root}>
        <Box display="flex" alignItems="center" mb={1}>
          <Typography className={classes.sectionTitle}>
            <span role="img" aria-label="relógio">🕐</span> Horário de Funcionamento
          </Typography>
          <Tooltip title="Define quando esta área de entrega aceita pedidos. Fora do horário, o cliente não poderá fazer pedidos para este local.">
            <IconButton size="small">
              <InfoIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        </Box>

        <FormControl
          fullWidth
          variant="outlined"
          size="small"
          className={classes.timezoneSelect}
        >
          <InputLabel>Fuso Horário</InputLabel>
          <Select
            value={timezone}
            onChange={this.handleTimezoneChange}
            label="Fuso Horário"
          >
            {BRAZIL_TIMEZONES.map((tz) => (
              <MenuItem key={tz.value} value={tz.value}>
                {tz.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider />

        {DAYS_OF_WEEK.map((day) => {
          const daySchedule = schedule[day.key] || {
            enabled: false,
            open: "08:00",
            close: "18:00",
          };

          return (
            <Box
              key={day.key}
              className={`${classes.dayRow} ${
                !daySchedule.enabled ? classes.closedDay : ""
              }`}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={daySchedule.enabled}
                    onChange={() => this.handleDayToggle(day.key)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography className={classes.dayLabel}>
                    {compact ? day.short : day.label}
                  </Typography>
                }
                style={{ marginRight: 8, minWidth: compact ? 80 : 150 }}
              />

              {daySchedule.enabled ? (
                <Grid container spacing={1} alignItems="center">
                  <Grid item>
                    <TextField
                      type="time"
                      value={daySchedule.open}
                      onChange={(e) =>
                        this.handleTimeChange(day.key, "open", e.target.value)
                      }
                      size="small"
                      variant="outlined"
                      className={classes.timeField}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }} // 5 min intervals
                    />
                  </Grid>
                  <Grid item>
                    <Typography variant="body2">até</Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      type="time"
                      value={daySchedule.close}
                      onChange={(e) =>
                        this.handleTimeChange(day.key, "close", e.target.value)
                      }
                      size="small"
                      variant="outlined"
                      className={classes.timeField}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                    />
                  </Grid>
                  <Grid item>
                    <Tooltip title="Copiar horário para todos os dias">
                      <IconButton
                        size="small"
                        onClick={() => this.copyToAllDays(day.key)}
                        className={classes.copyButton}
                      >
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Fechado
                </Typography>
              )}
            </Box>
          );
        })}

        <Typography className={classes.infoText}>
          * Pedidos fora do horário de funcionamento não serão aceitos para esta
          área.
        </Typography>
      </Box>
    );
  }
}

export { DEFAULT_BUSINESS_HOURS, DAYS_OF_WEEK, BRAZIL_TIMEZONES };
export default withStyles(styles)(BusinessHoursEditor);
